/*
 * A WORK CARD — the shape for a tool whose ARGUMENT is the deliverable.
 *
 * Most tool calls are a means: a query, a path, an id, and the interesting part is what came back.
 * A few are not. When the assistant authors a statechart, the argument IS the work — an SCXML
 * document being written — and the transcript drew it as a collapsed row saying "models.write",
 * with the document itself folded away behind a disclosure as raw JSON, arriving all at once when
 * the call finished. The most substantial thing the product does looked like its least.
 *
 * So: one skeleton, many bodies. The skeleton is here and is the same everywhere — a title, a
 * status next to it, a framed body — which is what makes an authored model, a dashboard and a
 * document read as the same kind of event rather than three inventions. What goes INSIDE is
 * registered per tool by whoever owns that domain: base-ui knows nothing about statecharts, and
 * the app that does registers a body that renders one.
 *
 * WHY A REGISTRY AND NOT A SWITCH. The renderer lives in base-ui and the thing rendered lives in
 * the app — a statechart component, a dashboard preview — and base-ui must not import either. Same
 * reason client tools are registered rather than listed: the surface that can do the thing declares
 * it.
 */
import { html, nothing } from 'lit';
import { t } from '../i18n';
/**
 * Pull one string field out of arguments that DO NOT PARSE YET.
 *
 * Mid-stream the JSON is truncated — the document is half-written — so `JSON.parse` fails for the
 * whole time the card is most interesting. This walks the raw text to the field, then unescapes as
 * far as it goes, which is what makes the SCXML appear as it is typed rather than at the end.
 *
 * Returns an empty string when the key has not arrived yet, which reads as "nothing written so far"
 * — true, and the right thing to show.
 */
export function partialString(raw, key) {
    const at = raw.indexOf(`"${key}"`);
    if (at < 0)
        return '';
    const q = raw.indexOf('"', raw.indexOf(':', at + key.length + 2) + 1);
    if (q < 0)
        return '';
    let out = '';
    for (let i = q + 1; i < raw.length; i++) {
        const c = raw[i];
        if (c === '\\') {
            const n = raw[i + 1];
            // A trailing lone backslash is a half-arrived escape: stop rather than print it.
            if (n === undefined)
                break;
            out += n === 'n' ? '\n' : n === 't' ? '\t' : n === 'r' ? '' : n === 'u' ? '' : n;
            // \uXXXX: skip the digits we cannot show yet rather than printing them as text.
            i += n === 'u' ? 5 : 1;
            continue;
        }
        // The closing quote — the field is complete.
        if (c === '"')
            break;
        out += c;
    }
    return out;
}
const cards = new Map();
/**
 * Claim a tool name. Returns the undo, so a shell that registers on connect can release on
 * disconnect and a second mount does not leave two.
 */
export function registerWorkCard(toolName, card) {
    cards.set(toolName, card);
    return () => {
        if (cards.get(toolName) === card)
            cards.delete(toolName);
    };
}
/** The card for this call, if one is registered AND it wants this particular call. */
export function workCardFor(tool) {
    const card = cards.get(tool.name);
    if (!card)
        return undefined;
    return card.when && !card.when(tool) ? undefined : card;
}
export function tokenizeXml(src) {
    const out = [];
    const push = (cls, text) => {
        if (!text)
            return;
        const last = out[out.length - 1];
        if (last && last.cls === cls)
            last.text += text;
        else
            out.push({ cls, text });
    };
    let i = 0;
    while (i < src.length) {
        const lt = src.indexOf('<', i);
        if (lt < 0) {
            push('x-text', src.slice(i));
            break;
        }
        push('x-text', src.slice(i, lt));
        /*
         * A COMMENT ENDS AT ITS OWN TERMINATOR, not at the first bracket.
         *
         * A statechart's comments routinely contain markup — an example transition, a state that was
         * tried and dropped — and scanning to the first closing bracket would end the comment inside it
         * and colour the rest of the document as if that markup were real.
         */
        if (src.startsWith('<!--', lt)) {
            const end = src.indexOf('-->', lt + 4);
            const body = end < 0 ? src.slice(lt) : src.slice(lt, end + 3);
            push('x-comment', body);
            i = lt + body.length;
            continue;
        }
        // An unterminated tag is the normal case mid-stream — take the rest and colour it as a tag.
        const gt = src.indexOf('>', lt);
        const tag = gt < 0 ? src.slice(lt) : src.slice(lt, gt + 1);
        // A declaration or doctype is one colour too; nothing inside it is markup.
        if (tag.startsWith('<!')) {
            push('x-comment', tag);
        }
        else {
            const m = /^<\/?[\w:.-]*/.exec(tag);
            push('x-tag', m ? m[0] : tag.slice(0, 1));
            let rest = tag.slice(m ? m[0].length : 1);
            // attr="value" pairs, then whatever is left (the closing bracket, stray whitespace).
            const attr = /([\w:.-]+)(\s*=\s*)("[^"]*"?|'[^']*'?)?/g;
            let at = 0;
            let a;
            while ((a = attr.exec(rest))) {
                push('x-tag', rest.slice(at, a.index));
                push('x-attr', a[1]);
                push('x-tag', a[2]);
                if (a[3])
                    push('x-val', a[3]);
                at = a.index + a[0].length;
            }
            push('x-tag', rest.slice(at));
            rest = '';
        }
        i = gt < 0 ? src.length : gt + 1;
    }
    return out;
}
/** The default body: the document, coloured, growing. What a card gets for free. */
export function renderXml(src) {
    return html `<pre class="wc-pre">${tokenizeXml(src).map((tk) => html `<span class=${tk.cls}>${tk.text}</span>`)}</pre>`;
}
/**
 * THE SKELETON.
 *
 * Deliberately thin: a title, a status word beside it, a framed body. Everything specific — what
 * the title says, what the body is, whether it becomes a live preview once the call lands — belongs
 * to the registered card, because that is the half base-ui cannot know.
 */
/** Which view this card is showing: the reader's choice if they made one, else the card's default. */
export function currentView(tool, card, h) {
    const fallback = card.defaultView?.(tool) ?? (tool.state === 'ok' ? 'preview' : 'code');
    if (!card.views || !h?.toolOpen?.(`view:${tool.id}`))
        return fallback;
    return fallback === 'preview' ? 'code' : 'preview';
}
const VIEW_ICON = { preview: 'workflow', code: 'code-xml' };
/** Whether this card's body is showing. Always, unless it folds: then while the call is in flight
 *  (and the turn is being looked at), or when the reader has opened it. */
export function cardOpen(tool, card, h) {
    if (!card.collapsible)
        return true;
    return (h?.toolOpen?.(`fold:${tool.id}`) ?? false) || (tool.state === 'running' && !h?.quiet);
}
export function renderWorkCard(tool, card, statusText, h) {
    const view = currentView(tool, card, h);
    const other = view === 'preview' ? 'code' : 'preview';
    /*
     * ONE ICON, NOT A WORD. The header used to read READY / WRITING / FAILED; a settled card now shows
     * the toggle to its other view instead, drawn as the same flat icon button every other control in
     * the product uses — the model icon to see the model, the code icon to see its source. The words
     * survive only where they carry information the icon cannot: a call still arriving, a refusal,
     * or a card that says something of its own (a question waiting on the reader).
     */
    const own = card.status?.(tool);
    const settled = tool.state === 'ok' && !own;
    const open = cardOpen(tool, card, h);
    const aside = 
    // A folded card has no body on show, so a toggle between two views of it would change nothing.
    card.views && settled && open
        ? html `<button
          class="wc-view"
          title=${other === 'preview' ? t('ai.viewModel') : t('ai.viewCode')}
          aria-label=${other === 'preview' ? t('ai.viewModel') : t('ai.viewCode')}
          @click=${() => h?.onToggleTool?.(`view:${tool.id}`)}
        >
          <vitesse-icon name=${VIEW_ICON[other]} size="14"></vitesse-icon>
        </button>`
        : settled
            ? nothing // settled and single-view: the frame says it, and READY beside a finished thing said nothing
            : html `<span class="wc-state">${own ?? statusText(tool)}</span>`;
    const fold = card.collapsible ? { open, toggle: () => h?.onToggleTool?.(`fold:${tool.id}`) } : undefined;
    return frame(tool.state, card.title(tool), aside, card.body(tool, view), card.summary?.(tool) ?? '', fold);
}
/**
 * A RUN OF CALLS AS ONE CARD — see `WorkCard.list`.
 *
 * The card's state, title and word come from the call the person is AT: the first one still
 * unsettled, or the last when every one has been answered. So a list of three questions reads as
 * waiting until the third is answered, and the frame turns settled at the same moment the list does.
 */
export function renderWorkList(tools, card, statusText) {
    const at = tools.find((x) => x.state !== 'ok') ?? tools[tools.length - 1];
    const aside = html `<span class="wc-state">${card.status?.(at) ?? statusText(at)}</span>`;
    return frame(at.state, card.title(at), aside, card.list(tools));
}
/**
 * The one skeleton both draw: a title, the thing beside it, a framed body.
 *
 * A foldable card's title and summary become the disclosure button — the line you click is the line
 * that describes it, as on a tool row — with the state word or toggle beside it, outside the
 * button, because a button inside a button is not a thing HTML allows. The body sits in the same
 * grid fold the tool rows use: always in the DOM, so it animates both ways rather than popping.
 */
function frame(state, title, aside, body, summary = '', fold) {
    const line = html `<span class="wc-title">${title}</span>${summary ? html `<span class="wc-summary" title=${summary}>${summary}</span>` : nothing}`;
    if (!fold) {
        return html `
      <div class="work-card ${state}">
        <div class="wc-head">
          ${line}
          ${aside}
        </div>
        <div class="wc-body">${body}</div>
      </div>
    `;
    }
    return html `
    <div class="work-card folds ${state} ${fold.open ? 'open' : ''}">
      <div class="wc-head">
        <button class="wc-fold" aria-expanded=${fold.open ? 'true' : 'false'} @click=${fold.toggle}>
          <span class="wc-chevron"><vitesse-icon name="chevron-right" size="12"></vitesse-icon></span>
          ${line}
        </button>
        ${aside}
      </div>
      <div class="wc-track">
        <div class="wc-clip"><div class="wc-body">${body}</div></div>
      </div>
    </div>
  `;
}
const fences = new Map();
/**
 * Claim a fence language ('scxml') with a CARD — the same card the tool path uses, so a document
 * that arrives as prose and one written through a tool are one component with one toggle, not two
 * things that happen to look similar. Returns the undo.
 *
 * The fence is presented to the card as a settled call whose one argument is the source, under the
 * key the tool would have used, so a card written for the tool needs no second code path.
 */
export function registerFenceRenderer(lang, card, argKey) {
    const key = lang.toLowerCase();
    const claim = { card: { ...card, title: (tool) => card.title(tool) || key }, argKey };
    fences.set(key, claim);
    return () => {
        if (fences.get(key) === claim)
            fences.delete(key);
    };
}
/** The renderer for this fence's language, if one is registered. */
export function fenceRendererFor(lang) {
    const claim = fences.get((lang || '').toLowerCase());
    if (!claim)
        return undefined;
    const { card, argKey } = claim;
    return (source, h) => {
        // Keyed on the source so two fences in one turn toggle independently and a repaint keeps each
        // one's view — a fence has no call id of its own.
        const tool = { id: fenceToolId(source), name: '', argumentsJson: JSON.stringify({ [argKey]: source }), state: 'ok' };
        return renderWorkCard(tool, card, () => '', h);
    };
}
/** The call id a fence stands in under — a pure function of its source, so a repaint finds the
 *  same view state and two fences in one turn toggle independently. */
export function fenceToolId(source) {
    return `fence:${hash(source)}`;
}
/** Small and stable, for keying. Not for security. */
function hash(s) {
    let h = 5381;
    for (let i = 0; i < s.length; i++)
        h = ((h << 5) + h + s.charCodeAt(i)) | 0;
    return (h >>> 0).toString(36);
}
//# sourceMappingURL=work-card.js.map